import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { type SvgIconProps } from '@mui/material'
import { styled } from '@mui/material/styles'
import SvgIcon from '@mui/material/SvgIcon'
import { TreeItem as MuiTreeItem, type TreeItemProps as MuiTreeItemProps, treeItemClasses } from '@mui/x-tree-view/TreeItem'
import { Box } from '@pets-ui/Box/Box'
import { Chip, type ChipColor } from '@pets-ui/Chip/Chip'
import { HStack } from '@pets-ui/HStack/HStack'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { type ComponentType, createElement, type MouseEvent, type PropsWithChildren } from 'react'

export interface MenuNode {
  label: string
  to?: string
  icon?: ComponentType<SvgIconProps>
  chip?: { label: string, color: ChipColor }
  nodes?: MenuNode[]
}
export interface TreeItemProps extends MuiTreeItemProps {
  root?: MenuNode
  navigate?: (path: string) => void
}

const defaultHighlightStyle = {
  content: '""',
  position: 'absolute',
  top: 0,
  bottom: 0,
  left: 0,
  right: 0,
  backgroundColor: colors.primary50,
  borderRadius: '4px',
  zIndex: -1,
}

const subItemHighlightStyle = {
  ...defaultHighlightStyle,
  left: '-16px',
}

const textHighlightStyle = {
  color: `${colors.primary600} !important`,
}

const StyledTreeItem = styled(MuiTreeItem)(({ theme }) => ({
  // This styles the container of the label and icon
  [`& .${treeItemClasses.content}`]: {
    flexDirection: 'row', // Ensure they are in a row
    justifyContent: 'space-between', // Spreads out the content to each end
    padding: theme.spacing(2), // Add padding to the focus area
    borderRadius: theme.shape.borderRadius,
    position: 'relative', // For pseudo-element positioning
    backgroundColor: 'transparent !important', // Override MUI's default backgrounds
  },

  [`& .${treeItemClasses.content}.Mui-selected .MuiTreeItem-label p, & .${treeItemClasses.content}.Mui-selected .MuiTreeItem-label h6`]: textHighlightStyle,
  [`& .${treeItemClasses.content}:hover .MuiTreeItem-label p, & .${treeItemClasses.content}:hover .MuiTreeItem-label h6`]: textHighlightStyle,
  // Use pseudo-element for stretched background on selected state (icon items)
  [`&.icon-item .${treeItemClasses.content}.Mui-selected::before`]: defaultHighlightStyle,
  [`&.icon-item .${treeItemClasses.content}:hover::before`]: defaultHighlightStyle,
  [`&.icon-item .${treeItemClasses.content}.Mui-focused::before`]: defaultHighlightStyle,

  // Use pseudo-element for stretched background on selected state (sub-items)
  [`&.sub-item .${treeItemClasses.content}.Mui-selected::before`]: subItemHighlightStyle,
  [`&.sub-item .${treeItemClasses.content}:hover::before`]: subItemHighlightStyle,
  [`&.sub-item .${treeItemClasses.content}.Mui-focused::before`]: subItemHighlightStyle,
  // This styles the icon container specifically
  [`& .${treeItemClasses.iconContainer}`]: {
    position: 'relative', // Change from absolute if we want it inline but on the right
    order: 2, // Flex order to ensure it moves to the end
  },
  // Hide icon container when there are no children
  [`&.no-children .${treeItemClasses.iconContainer}`]: {
    display: 'none',
  },
  // This styles the label container specifically
  [`& .${treeItemClasses.label}`]: {
    flexGrow: 1, // Allows the label to fill the space, pushing the icon to the end
    order: 1, // Ensures label comes before icon in flow
  },
}))

export const TreeItem = ({ root, navigate, ...rest }: PropsWithChildren<TreeItemProps>) => {
  const handleToggle = (_event: MouseEvent<HTMLElement>) => {
    if (root?.to) {
      navigate?.(root.to) // Navigate when a node is clicked
    }
  }
  const leftIcon = root?.icon ? createElement(root.icon, { sx: { mr: 1, color: colors.grey500 } }) : null
  const hasChildren = root?.nodes && root.nodes.length > 0
  const classNames = [
    leftIcon ? 'icon-item' : 'sub-item',
    !hasChildren && 'no-children',
  ].filter(Boolean).join(' ')
  return (
    <StyledTreeItem
      {...rest}
      onClick={handleToggle}
      className={classNames}
      expandIcon={hasChildren ? <ExpandMoreIcon sx={{ color: colors.grey600 }} /> : undefined}
      collapseIcon={hasChildren ? <ExpandLessIcon sx={{ color: colors.grey600 }} /> : undefined}
      label={(
        <HStack>
          {leftIcon && <SvgIcon sx={{ fontSize: 20 }}>{leftIcon}</SvgIcon>}
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <Box pl={leftIcon ? '7px' : '9px'} pr={0} pt={0} pb={0}>
              <Text color={leftIcon ? colors.grey500 : colors.grey900} variant={leftIcon ? 'subtitle' : 'body'}>{root?.label}</Text>
            </Box>
            {root?.chip && <Chip color={root?.chip?.color} size="small" borderRadius="4px">{root?.chip?.label}</Chip>}
          </div>
        </HStack>
      )}
    >
      {root?.nodes?.map((childConfig, index) => {
        return (
          <TreeItem
            key={`${childConfig.to}-${index}`}
            nodeId={`${childConfig.to}-${index}`}
            label={childConfig.label}
            root={childConfig}
            navigate={navigate}
          />
        )
      })}
    </StyledTreeItem>
  )
}
